/*
 * Julvärmen content pages (boards Contact, Track, Policy and their phone boards).
 *
 * <jv-track>  The track form signs the customer in to their customer account with the email filled in and brings
 *             them back to this page with ?order=<number>; signed-in customers then see their real orders, and this
 *             element shows the one they asked for (or the latest) without reloading.
 * <jv-legal>  Print button, the "Visa: …" picker on phones, smooth contents links, wide tables that scroll on
 *             desktop, and data-label on table cells (from the table head) for the stacked phone layout.
 * Forms       Topic chips keep .is-active in step for browsers without :has(), the submit button shows a spinner,
 *             and the success or error message of a posted contact form gets focus.
 *
 * Every component is a custom element that sets itself up in connectedCallback, so it keeps working after Section
 * Rendering and in the theme editor. Without JavaScript the pages still work: the track form posts to the sign-in
 * page, the contents are plain links and the policy text reads top to bottom.
 */
(() => {
  'use strict';

  if (window.JVPages) return;
  window.JVPages = { version: 1 };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  const focusWithoutScroll = (element) => {
    if (!element) return;
    if (!element.matches('a, button, input, select, textarea, summary, [tabindex]')) element.setAttribute('tabindex', '-1');
    element.focus({ preventScroll: true });
  };
  const orderKey = (value) =>
    String(value || '')
      .trim()
      .replace(/^#/, '')
      .replace(/\s+/g, '')
      .toUpperCase();
  const digits = (value) => String(value || '').replace(/\D/g, '');

  /* ------------------------------------------------------------------ track */
  class JvTrack extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-jv-track-form]');
      this.orderInput = this.querySelector('[data-jv-track-number]');
      this.emailInput = this.querySelector('[data-jv-track-email]');
      this.error = this.querySelector('[data-jv-track-error]');
      this.cards = [...this.querySelectorAll('article[data-jv-track-order]')];
      this.missing = this.querySelector('[data-jv-track-missing]');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event), options);
      [this.orderInput, this.emailInput].forEach((input) => {
        if (input) input.addEventListener('input', () => this.setError(''), options);
      });
      this.addEventListener('click', (event) => this.onClick(event), options);

      const requested = new URLSearchParams(window.location.search).get('order');
      if (requested && this.orderInput && !this.orderInput.value) this.orderInput.value = requested.startsWith('#') ? requested : `#${requested}`;
      if (this.cards.length) this.show(orderKey(requested), { focus: Boolean(requested) });
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
    }

    get signedInEmail() {
      return (this.dataset.customerEmail || '').trim().toLowerCase();
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const again = event.target.closest('[data-jv-track-again]');
      if (again) {
        event.preventDefault();
        if (this.orderInput) {
          this.orderInput.value = '';
          this.form.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
          this.orderInput.focus({ preventScroll: true });
        }
        return;
      }
      const pick = event.target.closest('[data-jv-track-pick]');
      if (pick) {
        event.preventDefault();
        if (this.orderInput) this.orderInput.value = `#${pick.dataset.jvTrackPick}`;
        this.show(pick.dataset.jvTrackPick, { focus: true, updateUrl: true });
      }
    }

    onSubmit(event) {
      event.preventDefault();
      const number = orderKey(this.orderInput ? this.orderInput.value : '');
      const email = (this.emailInput ? this.emailInput.value : '').trim();

      if (!email || (this.emailInput && !this.emailInput.checkValidity())) {
        this.setError(this.dataset.errorEmail || '', this.emailInput);
        return;
      }

      // Already signed in with this email: show the order from the list on the page.
      if (this.signedInEmail) {
        if (email.toLowerCase() !== this.signedInEmail) {
          this.setError(this.dataset.otherEmail || '', this.emailInput);
          return;
        }
        if (this.cards.length) {
          this.show(number, { focus: true, updateUrl: true });
          return;
        }
      }

      // Not signed in: go to Shopify's sign-in with the email filled in, then come back here with the number.
      const action = this.form.getAttribute('action') || '/customer_authentication/login';
      const url = new URL(action, window.location.origin);
      for (const [name, value] of new FormData(this.form)) {
        if (name === 'order' || typeof value !== 'string') continue;
        url.searchParams.set(name, value);
      }
      const back = new URL(this.dataset.returnPath || window.location.pathname, window.location.origin);
      if (number) back.searchParams.set('order', number);
      url.searchParams.set('return_to', back.pathname + back.search);
      url.searchParams.set('login_hint', email);
      const button = this.form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');
      window.location.assign(url.toString());
    }

    show(key, { focus = false, updateUrl = false } = {}) {
      if (!this.cards.length) return;
      let match = null;
      if (key) {
        match = this.cards.find((card) => card.dataset.jvTrackOrder === key);
        if (!match && digits(key)) match = this.cards.find((card) => digits(card.dataset.jvTrackOrder) === digits(key));
      } else {
        match = this.cards[0];
      }
      this.cards.forEach((card) => {
        card.hidden = card !== match;
      });
      if (this.missing) this.missing.hidden = Boolean(match);

      if (updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        if (key) url.searchParams.set('order', key);
        else url.searchParams.delete('order');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }

      const target = match ? match.querySelector('h2') : this.missing && this.missing.querySelector('h2');
      if (focus && target) {
        const card = match || this.missing;
        if (card.getBoundingClientRect().top < 0 || card.getBoundingClientRect().top > window.innerHeight * 0.7) {
          card.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        }
        focusWithoutScroll(target);
      }
    }

    setError(message, field) {
      if (this.error) {
        this.error.textContent = message;
        this.error.hidden = !message;
      }
      [this.orderInput, this.emailInput].forEach((input) => {
        if (!input) return;
        if (message && input === field) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });
      if (message && field) field.focus();
    }
  }
  define('jv-track', JvTrack);

  /* ------------------------------------------------------------------ legal pages */
  class JvLegal extends HTMLElement {
    connectedCallback() {
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.content = this.querySelector('[data-jv-legal-content]');
      this.picker = this.querySelector('[data-jv-legal-picker]');

      const print = this.querySelector('[data-jv-print]');
      if (print && typeof window.print === 'function') {
        print.hidden = false;
        print.addEventListener('click', () => window.print(), options);
      }

      if (this.content) this.prepareTables();

      this.addEventListener('click', (event) => this.onClick(event), options);
      document.addEventListener('click', (event) => this.onDocumentClick(event), options);
      document.addEventListener('keydown', (event) => this.onKeydown(event), options);
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('[data-jv-legal-toc] a[href^="#"]');
      if (!link) return;
      let id = link.hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch (error) {
        /* keep the raw id */
      }
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      focusWithoutScroll(target);
      if (window.history.replaceState) window.history.replaceState(window.history.state, '', `#${id}`);
    }

    onDocumentClick(event) {
      if (this.picker && this.picker.open && event.target instanceof Node && !this.picker.contains(event.target)) {
        this.picker.open = false;
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.picker || !this.picker.open) return;
      this.picker.open = false;
      const summary = this.picker.querySelector('summary');
      if (summary) summary.focus();
    }

    // Cells get data-label from the table head when the page text has none, so the phone layout can label them;
    // wide tables scroll sideways on larger screens instead of squeezing.
    prepareTables() {
      this.content.querySelectorAll('table').forEach((table) => {
        const headCells = [...table.querySelectorAll('thead th, thead td')];
        const labels = headCells.map((cell) => cell.textContent.trim());
        if (labels.length) {
          table.querySelectorAll('tbody tr').forEach((row) => {
            [...row.children].forEach((cell, index) => {
              if (!cell.hasAttribute('data-label') && labels[index] !== undefined) cell.setAttribute('data-label', labels[index]);
            });
          });
        }
        const parent = table.parentElement;
        if (!parent || parent.classList.contains('jv-legal__scroll')) return;
        const wrap = document.createElement('div');
        wrap.className = 'jv-legal__scroll';
        parent.insertBefore(wrap, table);
        wrap.append(table);
      });
    }
  }
  define('jv-legal', JvLegal);

  /* ------------------------------------------------------------------ forms */
  // Topic chips: mirror the checked radio in .is-active (for browsers without :has()).
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const group = input.closest('[data-jv-topics]');
    if (!group) return;
    group.querySelectorAll('.jv-topic').forEach((chip) => {
      const radio = chip.querySelector('input');
      chip.classList.toggle('is-active', Boolean(radio && radio.checked));
    });
  });

  // Busy spinner on the submit button while the form posts.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-jv-busy]')) return;
    window.setTimeout(() => {
      if (event.defaultPrevented) return;
      const button = form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');
    }, 0);
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-jv-busy] [aria-busy], [data-jv-track-form] [aria-busy]').forEach((button) => button.removeAttribute('aria-busy'));
  });

  // A posted contact form: bring its message into view and focus it.
  const focusPosted = () => {
    const target = document.querySelector('[data-jv-focus-on-load]');
    if (!target) return;
    target.scrollIntoView({ behavior: 'auto', block: 'center' });
    focusWithoutScroll(target);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', focusPosted, { once: true });
  else focusPosted();
})();
